import { FormInstance } from 'antd';
import { sum, sumBy } from 'lodash';
import shopService from 'modules/shop/services';
import { useEffect } from 'react';
import { parseDecimal } from 'utils/number';
import { CASHIER_ITEM_TYPE } from '../cashier.constants';
import { IUICashierFormValues } from '../cashier.interface';
import { calcDiscountForItem } from '../cashier.utils';
import { parseFee } from 'utils/formatCurrency';

const useCalculateCashier = (form: FormInstance<any>) => {
  const settingTax = shopService.selectors.settingTax();
  const cashIncentive = shopService.selectors.cashIncentive();
  const feeCreditCard = shopService.selectors.feeCreditCard();

  useEffect(() => {
    form.setFieldsValue({
      feeCreditCard,
      settingTax,
      chargeFee: !!feeCreditCard?.enable,
      cashIncentive,
      cashIncentiveDiscount: !!cashIncentive?.enable,
    } as IUICashierFormValues);
  }, [feeCreditCard, settingTax, cashIncentive]);

  const calcDiscountLoyalty = (subTotal: number, values: IUICashierFormValues) => {
    if (!values.membership) return 0;
    const { discountType, discountValue } = values.membership;
    if (discountType === 'PERCENT') return Math.round(subTotal * (discountValue || 0) / 100);
    return discountValue || 0;
  };

  const calcFeeCreditCardAmount = (subTotal: number, values: IUICashierFormValues) => {
    if (!values.chargeFee) return 0;
    if (values.feeCreditCard?.feeType === 'PERCENT') {
      return subTotal * (values.feeCreditCard?.feeValue || 0) / 100;
    }
    return values.feeCreditCard?.feeValue || 0;
  };

  const calcCashDiscountAmount = (subTotal: number, values: IUICashierFormValues) => {
    if (!values.cashIncentiveDiscount) return 0;
    if (values.cashIncentive?.discountType === 'PERCENT') {
      return Math.round(subTotal * (values.cashIncentive?.discountValue || 0) / 100);
    }
    return values.cashIncentive?.discountValue || 0;
  };

  const calc = () => {
    const values = form.getFieldsValue() as IUICashierFormValues;
    const subTotal = parseDecimal(sumBy(values.items, o => (o.price || 0) * (o.quantity || 0))) || 0;
    const balance = subTotal;

    const subtotalForTax = sumBy(values.items?.filter(o => o.itemType === CASHIER_ITEM_TYPE.PRODUCT && !!o.activeTax), (o) => (o.price || 0) * (o.quantity || 0));

    const discountByItem = sumBy(values.items, o => calcDiscountForItem(o)) || 0;

    const discountLoyalty = calcDiscountLoyalty(subTotal, values);

    const discountByTicket = (values.discount || 0) + discountLoyalty;

    const subTotalBalance = subTotal - (discountByTicket + discountByItem);

    const amountForTax = subtotalForTax > 0 ? (subtotalForTax ?? 0) - (discountByTicket + discountByItem) : 0;

    let saleTax = 0;
    if (settingTax?.enableSaleTax) {
      const _saleTax = parseFee(amountForTax * (settingTax.saleTaxPercent / 100));
      saleTax = _saleTax > 0 ? _saleTax : 0;
    }

    let useTax = 0;
    if (settingTax?.enableUsedTax) {
      const _useTax = parseFee(amountForTax * (settingTax.usedTaxPercent / 100));
      useTax = _useTax > 0 ? _useTax : 0;
    }

    const feeCreditCardAmount = calcFeeCreditCardAmount(subTotal, values);
    const cashIncentiveAmount = calcCashDiscountAmount(subTotal, values);

    const _total = parseFee(sum([
      subTotal,
      saleTax,
      useTax,
      -discountByTicket,
      -discountByItem,
    ]));

    form.setFieldsValue({
      subTotal,
      balance,
      subTotalBalance,
      total: _total > 0 ? _total : 0,
      saleTax,
      useTax,
      feeCreditCardAmount,
      cashIncentiveAmount,
      discountTotal: discountByTicket + discountByItem,
      discountByItem,
      discountByTicket,
    });
  };
  const saveSettingConfigs = () => ({
    feeCreditCard,
    settingTax,
    chargeFee: !!feeCreditCard?.enable,
    cashIncentive,
    cashIncentiveDiscount: !!cashIncentive?.enable,
  } as IUICashierFormValues);

  return {
    calculateCashier: calc,
    saveSettingConfigs,
  };
};

export default useCalculateCashier;
